// Explicit state for a per-Request read.
//
// `$subTickets === null` cannot carry this: it means "no list has ever been applied", which is
// simultaneously "still loading", "loaded and empty" and "the read failed". Anything that has to
// answer "is this phase genuinely absent from this Request?" — as the running-timer label does —
// needs those three told apart, because two of them mean "not yet known" and only one means "no".
export type SubTicketReadStatus = 'idle' | 'loading' | 'loaded' | 'error';

// Kept for the existing consumers that only ask about the list
export type SubTicketListStatus = SubTicketReadStatus;

export interface SubTicketReadState {
    status: SubTicketReadStatus;

    // Which Request the status describes. A settled read for the PREVIOUS Request says nothing
    // about this one, so every consumer compares this before trusting `status` — that is what
    // stops a stale task or filter completion answering for the task now on screen.
    taskId: ID | null;
}

export type SubTicketListReadState = SubTicketReadState;

export const INITIAL_SUB_TICKET_READ: SubTicketReadState = { status: 'idle', taskId: null };
export const INITIAL_SUB_TICKET_LIST_READ = INITIAL_SUB_TICKET_READ;

// Settled means the server has answered for THIS Request, one way or the other. A failed read is
// settled too: it will not become a list on its own, so waiting on it forever is its own bug.
export const isSubTicketReadSettledFor = (read: SubTicketReadState, taskId: ID): boolean =>
    read.taskId === taskId && (read.status === 'loaded' || read.status === 'error');

export const isSubTicketListSettledFor = isSubTicketReadSettledFor;

// Loaded, and loaded for the Request being asked about. Anything held for another Request is
// withheld rather than shown: it names people who may have no business on this one.
export const isSubTicketReadCurrentFor = (read: SubTicketReadState, taskId: ID): boolean =>
    read.taskId === taskId && read.status === 'loaded';
